import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { UpdatedParam } from "event-definitions"
import { useLocation } from "react-router-dom"
import { resolveAsFunction } from "lib/resolve-as-function"

/**
 * Retrieves and updates a search parameter from the URL.
 *
 * @param {string} key - The key of the search parameter.
 * @param {string} [defaultValue=""] - The default value to use if the search parameter is not found.
 * @param {function} [transform=(v) => v] - The transformation function to apply to the search parameter value.
 * @returns {array} - An array containing the transformed search parameter value and a setter function.
 */
export function useSearchParam(key, defaultValue = "", transform = (v) => v, options = {}) {
    const { push = false } = options
    const mounted = useRef(true)
    const [value, setValue] = useState(
        () => Object.fromEntries(new URLSearchParams(window.location.search).entries())[key] ?? defaultValue
    )
    const location = useLocation()
    useEffect(() => {
        setValue(Object.fromEntries(new URLSearchParams(window.location.search).entries())[key] ?? defaultValue)
    }, [defaultValue, key, location])
    UpdatedParam(key).useEvent(setValue)
    // eslint-disable-next-line
    const doSet = useCallback(set, [key, push])
    useLayoutEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])
    return [transform(value), doSet]

    function set(v) {
        if (!mounted.current) return
        const lastParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())
        if (typeof v === "function") {
            v = v(transform(lastParams[key] ?? defaultValue))
        }
        const stringValue = v === null || v === undefined ? "" : String(v)
        if (stringValue !== (lastParams[key] ?? "")) {
            if (stringValue === "") {
                delete lastParams[key]
            } else {
                lastParams[key] = stringValue
            }
            store(lastParams)
            UpdatedParam(key).raise(v)
        }
    }

    function store(lastParams) {
        const method = push ? "pushState" : "replaceState"
        const search = Object.entries(lastParams)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join("&")
        window.history[method]({}, null, `${window.location.pathname}${search ? "?" + search : ""}`)
    }
}

export function useSearchParamsBatch() {
    const mounted = useRef(true)

    useLayoutEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    return useCallback((updates, options = {}) => {
        if (!mounted.current) return
        const { push = false } = options
        const lastParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())
        let changed = false
        Object.entries(updates).forEach(([key, value]) => {
            const stringValue = value === null || value === undefined ? "" : String(value)
            if (stringValue !== (lastParams[key] ?? "")) {
                if (stringValue === "") {
                    delete lastParams[key]
                } else {
                    lastParams[key] = stringValue
                }
                changed = true
                UpdatedParam(key).raise(value)
            }
        })
        if (changed) {
            const method = push ? "pushState" : "replaceState"
            const search = Object.entries(lastParams)
                .filter(([, v]) => v !== undefined && v !== null && v !== "")
                .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
                .join("&")

            window.history[method]({}, null, `${window.location.pathname}${search ? "?" + search : ""}`)
        }
    }, [])
}

export function useNumericSearchParam(key, defaultValue, options) {
    return useSearchParam(key, defaultValue, (v) => +v, options)
}

export function useObjectSearchParam(key, defaultValue, options) {
    defaultValue = encodeURIComponent(JSON.stringify(defaultValue))
    const [value, setValue] = useSearchParam(key, defaultValue, (v) => v, options)

    const objectValue = JSON.parse(decodeURIComponent(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setObjectValue = useCallback(_setObjectValue, [setValue, value])
    return [objectValue, setObjectValue]

    function _setObjectValue(newValue) {
        newValue = resolveAsFunction(newValue)
        setValue(JSON.stringify(newValue(objectValue)))
    }
}
